import './styles.css';
import { LayoutHeader as Header } from '@/layouts';
import { useTheme } from '@/contexts/ThemeContext';
import { layout4ThemeStyle } from './theme';
import { LayoutFooter as Footer } from '@/layouts';
import { ServiceModal } from '@/components/ServiceModal';
import { Button } from '@/components/ui/button';
import { useAppData } from '@/contexts/AppDataContext';
import { openWhatsApp } from '@/lib/whatsapp';
import { useStore } from '@/contexts/StoreContext';
import {
  CreditCard,
  Shield,
  FileText,
  Video,
  MessageCircle,
  Wrench,
  Search,
  Users,
  Package,
  Settings,
  Gauge,
  Handshake,
  HandCoins,
  Phone,
  TrendingUp
} from 'lucide-react';

import videoCall from '@/assets/services/videoCall.png';
import valuation from '@/assets/services/valuation.png';
import consignment from '@/assets/services/consignment.png';
import consortium from '@/assets/services/consortium.png';
import forwardingAgent from '@/assets/services/forwardingAgent.png';
import financing from '@/assets/services/financing.png';
import partsAccessories from '@/assets/services/partsAccessories.png';
import scheduleReview from '@/assets/services/scheduleReview.png';
import insurance from '@/assets/services/insurance.png';
import scheduleServices from '@/assets/services/scheduleServices.png';
import fipeTable from '@/assets/services/fipeTable.png';
import testdrive from '@/assets/services/testdrive.png';
import directSales from '@/assets/services/directSales.png';

const Services = () => {
  const { config } = useAppData();
  const { selectedStore } = useStore();
  const store = selectedStore || config.stores[0];
  const services = store.services || [];
  const { colors } = useTheme();
  const themeStyle = layout4ThemeStyle(colors);
  const buttonColor = colors.button;

  const getServiceImage = (service: any, defaultImage: string) => {
    return service.image || defaultImage;
  };

  const serviceInfo: Record<string, { icon: any; title: string; description: string; image: string }> = {
    '65da42048eca108d87c91da6': { icon: Video, title: 'Agendar Videochamada', description: 'Agende uma videochamada para conhecer nossos veículos sem sair de casa', image: videoCall },
    '65dcb7548eca108d87c91dbb': { icon: Search, title: 'Avaliação', description: 'Avaliação profissional do seu veículo com preço justo de mercado', image: valuation },
    '65dcb7c58eca108d87c91dbd': { icon: HandCoins, title: 'Consignação', description: 'Deixe seu veículo em consignação conosco e venda com segurança', image: consignment },
    '65dcef468eca108d87c91dc8': { icon: Users, title: 'Consórcio', description: 'Adquira seu veículo através de consórcio com as melhores condições', image: consortium },
    '65dcb6528eca108d87c91db9': { icon: FileText, title: 'Despachante', description: 'Serviços de despachante para documentação veicular completa', image: forwardingAgent },
    '65dcb7898eca108d87c91dbc': { icon: CreditCard, title: 'Financiamento', description: 'Análise de crédito e financiamento com as melhores taxas do mercado', image: financing },
    '6628fd535bda2ae51f6fc5ff': { icon: Package, title: 'Peças e Acessórios', description: 'Peças originais e acessórios para seu veículo', image: partsAccessories },
    '65dcef098eca108d87c91dc7': { icon: Wrench, title: 'Revisões', description: 'Revisões preventivas e corretivas com equipe especializada', image: scheduleReview },
    '65dcb7238eca108d87c91dba': { icon: Shield, title: 'Seguros', description: 'Cotação de seguros com as principais seguradoras do país', image: insurance },
    '65dceeac8eca108d87c91dc6': { icon: Settings, title: 'Serviços', description: 'Serviços automotivos especializados para seu veículo', image: scheduleServices },
    '65dcb7fe8eca108d87c91dbe': { icon: TrendingUp, title: 'Tabela FIPE', description: 'Consulte o valor do seu veículo pela tabela FIPE', image: fipeTable },
    '65dcb5e78eca108d87c91db8': { icon: Gauge, title: 'Test-Drive', description: 'Agende um test drive e experimente o veículo dos seus sonhos', image: testdrive },
    '65dcb8548eca108d87c91dbf': { icon: Handshake, title: 'Vendas Diretas', description: 'Vendas diretas com as melhores condições e atendimento personalizado', image: directSales }
  };

  const getServiceInfo = (serviceId: string) => {
    return serviceInfo[serviceId] || {
      icon: MessageCircle,
      title: 'Serviço',
      description: 'Entre em contato para mais informações sobre este serviço',
      image: ''
    };
  };

  const getServiceButtonText = (serviceId: string) => {
    const buttonTexts: Record<string, string> = {
      '65da42048eca108d87c91da6': 'Agendar',
      '65dceeac8eca108d87c91dc6': 'Agendar',
      '65dcef098eca108d87c91dc7': 'Agendar',
      '65dcb5e78eca108d87c91db8': 'Agendar',
      '65dcb7898eca108d87c91dbc': 'Simular',
      '65dcb7548eca108d87c91dbb': 'Avaliar',
      '65dcb7c58eca108d87c91dbd': 'Avaliar',
      '65dcb7238eca108d87c91dba': 'Cotar',
      '65dcb6528eca108d87c91db9': 'Cotar',
      '6628fd535bda2ae51f6fc5ff': 'Cotar',
      '65dcef468eca108d87c91dc8': 'Solicitar',
      '65dcb8548eca108d87c91dbf': 'Solicitar',
      '65dcb7fe8eca108d87c91dbe': 'Consultar',
    };
    return buttonTexts[serviceId] || 'Solicitar Serviço';
  };

  return (
    <div className="layout-4 min-h-screen bg-[var(--wl4-bg)]" style={themeStyle}>
      <Header config={config} />

      <div className="wl4-page-banner">
        <div className="wl4-container">
          <p className="wl4-eyebrow mb-2">Serviços</p>
          <h1>Nossos Serviços</h1>
          <p className="mt-2 max-w-xl">
            Financiamento, avaliação, consignação e tudo para facilitar a compra ou venda do seu veículo.
          </p>
        </div>
      </div>

      <section className="wl4-section bg-white !pt-12 !pb-16">
        <div className="wl4-container">
          <div className="wl4-section-header !mb-8">
            <div>
              <p className="wl4-eyebrow">Soluções completas</p>
              <h2 className="wl4-h2 mt-1">Tudo para fechar negócio</h2>
              <p className="wl4-lead mt-1 max-w-lg text-sm">
                {services.length} {services.length === 1 ? 'serviço disponível' : 'serviços disponíveis'} para você
              </p>
            </div>
          </div>

          {services.length > 0 ? (
            <div className="wl4-service-grid">
              {services.map((service: any) => {
                const info = getServiceInfo(service.service);
                const IconComponent = info.icon;
                const serviceImage = getServiceImage(service, info.image);

                return (
                  <article key={service.service} className="wl4-service-card">
                    {serviceImage && (
                      <div className="wl4-service-card-media">
                        <img src={serviceImage} alt={info.title} loading="lazy" />
                        <div className="wl4-service-card-icon">
                          <IconComponent className="h-5 w-5" />
                        </div>
                      </div>
                    )}
                    <div className="wl4-service-card-body">
                      <h3>{info.title}</h3>
                      <p>{info.description}</p>
                      <ServiceModal service={service} serviceTitle={info.title} config={config}>
                        <Button
                          className="w-full rounded-lg text-white font-semibold h-10 mt-auto"
                          style={{ backgroundColor: buttonColor }}
                        >
                          {getServiceButtonText(service.service)}
                        </Button>
                      </ServiceModal>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 px-4 bg-[var(--wl4-bg)] rounded-xl border border-[var(--wl4-border)]">
              <p className="text-[var(--wl4-muted)]">Nenhum serviço configurado no momento.</p>
            </div>
          )}

          <div className="wl4-services-help mt-12 pt-10 border-t border-[var(--wl4-border)] flex flex-col md:flex-row md:items-center justify-between gap-6 text-center md:text-left">
            <div>
              <h2 className="wl4-h2">Precisa de ajuda?</h2>
              <p className="text-[var(--wl4-muted)] mt-2 max-w-md mx-auto md:mx-0">
                Nossa equipe está pronta para orientar você no serviço ideal.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center shrink-0">
              <Button
                size="lg"
                className="rounded-lg text-white font-semibold h-12 px-6"
                style={{ backgroundColor: buttonColor }}
                onClick={() => openWhatsApp(store.whatsapp?.ios)}
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                WhatsApp
              </Button>
              {store.phone?.ios && (
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-lg border-[var(--wl4-border)] text-[var(--wl4-text)] h-12 px-6 hover:bg-[var(--wl4-bg)]"
                  onClick={() => { window.location.href = store.phone.ios; }}
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Ligar
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer config={config} />
    </div>
  );
};

export default Services;
